import { useEffect, useState } from 'react';
import { updateDoc, doc } from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import { getCollection } from 'src/api';
import { Collection } from 'src/enum';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface WidgetTitleEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  widgetId: string;
  workspaceId: string;
  title: string;
  onTitleUpdated?: (newTitle: string) => void;
}

export default function WidgetTitleEditDialog({
  isOpen,
  onClose,
  widgetId,
  workspaceId,
  title,
  onTitleUpdated,
}: WidgetTitleEditDialogProps) {
  const { t } = useTranslation();
  const [editTitle, setEditTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initialize edit title when dialog opens
  useEffect(() => {
    if (isOpen) {
      setEditTitle(title);
    }
  }, [isOpen]);

  const handleSaveTitle = async () => {
    if (!editTitle.trim() || !workspaceId || !widgetId) {
      console.error('Missing required fields');
      toast.error(t('general.error.missingFields'));
      return;
    }

    setIsSaving(true);
    try {
      // Update Firebase directly
      const widgetRef = doc(DB, `workspaceReviews/${workspaceId}/integrations`, widgetId);
      await updateDoc(widgetRef, {
        title: editTitle.trim(),
      });

      toast.success(t('widgets.title.updateSuccess'));
      onTitleUpdated?.(editTitle.trim());
      onClose();
    } catch (error) {
      console.error('Error updating widget title:', error);
      toast.error(t('widgets.title.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditTitle(title);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('widgets.title.edit.title')}</DialogTitle>
          <DialogDescription>
            {t('widgets.title.edit.description', { widgetTitle: title })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="title" className="text-right">
              {t('transformations.title')}
            </Label>
            <Input
              id="title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="col-span-3"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') handleCancel();
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t('general.cancel')}
          </Button>
          <Button onClick={handleSaveTitle} disabled={isSaving}>
            {isSaving ? `${t('general.loading')}...` : t('general.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
